import axios from 'axios';
import { cognitoUserManager } from '@/config/authConfig';
import { BACKEND_API_URL } from '@/config/constants';
import { ROUTE_ALIASES } from '@/types';

let globalAxiosInterceptorsRegistered = false;

function registerGlobalAxiosInterceptorsOnce(): void {
  if (globalAxiosInterceptorsRegistered) {
    return;
  }
  globalAxiosInterceptorsRegistered = true;

  axios.interceptors.request.use(async (config) => {
    const user = await cognitoUserManager.getUser();
    if (user && user.access_token) {
      config.headers.Authorization = `Bearer ${user.access_token}`;
    }
    const isFormData = typeof FormData !== 'undefined' && config.data instanceof FormData;
    if (!isFormData && !config.headers['Content-Type']) {
      config.headers['Content-Type'] = 'application/json';
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        try {
          await cognitoUserManager.removeUser();
        } catch (error: unknown) {
          console.error('Error removing user:', error);
        }
        window.location.assign(ROUTE_ALIASES.LOGIN);
      }
      return Promise.reject(error);
    }
  );
}

export class ApiService {
  constructor() {
    registerGlobalAxiosInterceptorsOnce();
  }

  protected async get<T>(url: string): Promise<{ data: T }> {
    const response = await axios.get<T>(`${BACKEND_API_URL}${url}`);
    return response;
  }

  protected async put<T>(url: string, data: unknown): Promise<{ data: T }> {
    const response = await axios.put<T>(`${BACKEND_API_URL}${url}`, data);
    return response;
  }

  protected async delete<T>(url: string): Promise<{ data: T }> {
    const response = await axios.delete<T>(`${BACKEND_API_URL}${url}`);
    return response;
  }

  protected async post<T>(url: string, data: unknown): Promise<{ data: T }> {
    const response = await axios.post<T>(`${BACKEND_API_URL}${url}`, data);
    return response;
  }

  protected async postForm<T>(url: string, formData: FormData): Promise<{ data: T }> {
    const response = await axios.post<T>(`${BACKEND_API_URL}${url}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  }

  protected async getBlob(url: string): Promise<Blob> {
    const response = await axios.get(`${BACKEND_API_URL}${url}`, {
      responseType: 'blob',
    });
    return response.data;
  }

  protected async postBlob(url: string, data: unknown): Promise<Blob> {
    const response = await axios.post(`${BACKEND_API_URL}${url}`, data, {
      responseType: 'blob',
    });
    return response.data;
  }
}
